import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function NavigationBar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [language, setLanguage] = useState("th");

  // Use React Router to get the current path
  const location = useLocation();

  // Toggle the mobile menu
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Language-specific text
  const textContent = {
    th: {
      home: "หน้าหลัก",
      products: "สินค้าของเรา",
      awards: "รางวัล & คุณภาพ",
      blog: "บทความ",
      contact: "ติดต่อเรา",
      company: "Pornkamon Rice Flour Mills Co., Ltd.",
    },
    en: {
      home: "Home",
      products: "Products",
      awards: "Awards & Quality",
      blog: "Blog",
      contact: "Contact",
      company: "Pornkamon Rice Flour Mills Co., Ltd.",
    },
  };

  // Define navigation links
  const navLinks = [
    { path: "/", labelTh: textContent.th.home, labelEn: textContent.en.home },
    {
      path: "/products",
      labelTh: textContent.th.products,
      labelEn: textContent.en.products,
    },
    {
      path: "/awards",
      labelTh: textContent.th.awards,
      labelEn: textContent.en.awards,
    },
    { path: "/blog", labelTh: textContent.th.blog, labelEn: textContent.en.blog },
    {
      path: "/contact",
      labelTh: textContent.th.contact,
      labelEn: textContent.en.contact,
    },
  ];

  // Framer Motion variants for the mobile menu slide-in
  const menuVariants = {
    hidden: { opacity: 0, x: "100%" },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      x: "100%",
      transition: { duration: 0.3, ease: "easeIn" },
    },
  };

  // Hamburger line variants (morphing into X)
  const line1Variants = {
    closed: { rotate: 0, y: 0 },
    open: { rotate: 45, y: 7 },
  };
  const line2Variants = {
    closed: { opacity: 1 },
    open: { opacity: 0 },
  };
  const line3Variants = {
    closed: { rotate: 0, y: 0 },
    open: { rotate: -45, y: -7 },
  };

  // Handle language changes
  const handleLanguageChange = (e) => setLanguage(e.target.value);

  // Apply a white underline on desktop if the current path is active
  const getLinkClasses = (path) => {
    const baseClasses = "hover:text-yellow-400 transition-colors";
    const activeClasses = "border-b-2 border-white pb-1";
    return location.pathname === path
      ? `${baseClasses} ${activeClasses}`
      : baseClasses;
  };

  return (
    <header className="fixed top-0 w-full h-[66px] bg-blue-900 text-white flex items-center justify-between px-4 md:px-8 shadow-md z-50">
      {/* Logo + Company Name */}
      <div className="flex items-center">
        <img
          className="h-10 md:h-12 cursor-pointer hover:opacity-80 transition-opacity"
          src="/LOGO.png"
          alt="logo"
          onClick={() => (window.location.href = "/")}
        />
        <h1 className="ml-4 font-semibold text-lg md:text-xl whitespace-nowrap">
          {textContent[language].company}
        </h1>
      </div>

      {/* Desktop Navigation */}
      <nav className="hidden md:flex items-center space-x-8 text-base font-medium">
        {navLinks.map((link) => (
          <Link key={link.path} to={link.path} className={getLinkClasses(link.path)}>
            {language === "th" ? link.labelTh : link.labelEn}
          </Link>
        ))}
      </nav>

      {/* Desktop Language + (Hamburger if you still want it on desktop) */}
      <div className="hidden md:flex items-center space-x-4">
        {/* Language Selector */}
        <select
          onChange={handleLanguageChange}
          value={language}
          className="bg-blue-900 text-white text-base focus:outline-none border-none"
        >
          <option value="th">TH</option>
          <option value="en">EN</option>
        </select>

        {/* Optional Hamburger Icon for Desktop (usually hidden though) */}
        <div
          className="md:hidden flex flex-col items-center justify-center w-6 h-6 cursor-pointer"
          onClick={toggleMenu}
          aria-label="Toggle Menu"
        >
          <motion.span
            className="block w-6 h-[2px] bg-white mb-1"
            variants={line1Variants}
            animate={isMenuOpen ? "open" : "closed"}
            transition={{ duration: 0.3 }}
          />
          <motion.span
            className="block w-6 h-[2px] bg-white mb-1"
            variants={line2Variants}
            animate={isMenuOpen ? "open" : "closed"}
            transition={{ duration: 0.3 }}
          />
          <motion.span
            className="block w-6 h-[2px] bg-white"
            variants={line3Variants}
            animate={isMenuOpen ? "open" : "closed"}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Mobile: Language + Hamburger */}
      <div className="flex md:hidden items-center space-x-4">
        {/* Language Selector for Mobile */}
        <select
          onChange={handleLanguageChange}
          value={language}
          className="bg-blue-900 text-white text-lg focus:outline-none border-none"
        >
          <option value="th">TH</option>
          <option value="en">EN</option>
        </select>

        {/* Hamburger Icon */}
        <div
          className="flex flex-col items-center justify-center w-6 h-6 cursor-pointer"
          onClick={toggleMenu}
          aria-label="Toggle Menu"
        >
          <motion.span
            className="block w-6 h-[2px] bg-white mb-1"
            variants={line1Variants}
            animate={isMenuOpen ? "open" : "closed"}
            transition={{ duration: 0.3 }}
          />
          <motion.span
            className="block w-6 h-[2px] bg-white mb-1"
            variants={line2Variants}
            animate={isMenuOpen ? "open" : "closed"}
            transition={{ duration: 0.3 }}
          />
          <motion.span
            className="block w-6 h-[2px] bg-white"
            variants={line3Variants}
            animate={isMenuOpen ? "open" : "closed"}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Mobile Menu (framer-motion) */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.nav
            className="absolute top-[66px] right-0 w-[75%] max-w-sm h-screen bg-blue-900 py-6 px-8 flex flex-col space-y-6 text-lg font-medium"
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="hover:text-yellow-400 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {language === "th" ? link.labelTh : link.labelEn}
              </Link>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
